import { API_ORIGIN_URL } from '../../api/config';
import fetchService from '../fetchService';

import { BackendUserSchema, UsageLimitsResponse, UserPlan } from '../../types/user-schema';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('USER_SERVICE');

export interface UserProfile extends BackendUserSchema {}

export interface UserProfileUpdate {
  name?: string;
  avatar?: string;
  onboardingState?: any;
  backgroundPreference?: string;
  sharedComputerUse?: boolean;
  preferences?: {
    defaultModel?: string;
    fullOSEnabled?: boolean;
    timezone?: string;
    chatComputerUse?: boolean;
    os?: any;
    docs?: {
      autoConvertArrows?: boolean;
      autoConvertQuotes?: boolean;
      autoConvertFractions?: boolean;
      autoConvertDashes?: boolean;
      smartPunctuation?: boolean;
      pinnedTools?: string[];
      defaultFont?: string;
      defaultPageMode?: 'page' | 'pageless';
      [key: string]: any;
    };
    /** Apps pinned to the entity-sidebar header. List of app ids from
     *  `PINNABLE_APPS` (see `components/Space/EntitySidebar/pinnedApps.ts`).
     *  User-level setting carried across spaces. */
    pinnedApps?: string[];
    [key: string]: any;
  };
  [key: string]: any;
}

// Clean usage response format - follows entities.py convention
export interface UsageResponse {
  plan: UserPlan;
  usage: UsageLimitsResponse;
}

export class UserService {
  // Resolve base URL dynamically to reflect current API origin without requiring a full reload
  private get baseUrl(): string {
    try {
      // Prefer runtime override first, then window config, then compiled fallback
      const fromLocalStorage = (() => {
        try { return window.localStorage.getItem('API_ORIGIN_URL') || undefined; } catch { return undefined; }
      })();
      const fromWindow = (window as any)?.__CONFIG__?.API_ORIGIN_URL as string | undefined;
      const origin = fromLocalStorage || fromWindow || API_ORIGIN_URL || window.location.origin;
      return `${origin}/api/user`;
    } catch {
      return `${API_ORIGIN_URL}/api/user`;
    }
  }

  private getAuthHeaders() {
    const authToken = localStorage.getItem('authToken');
    return {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    };
  }

  async getUserProfile(): Promise<UserProfile> {
    try {
      // ✅ PERFORMANCE FIX: Use userStore as single source of truth
      // This prevents 20+ redundant API calls from multiple components
      const useUserStore = (await import('../../stores/userStore')).default;
      const { profile, fetchProfile } = useUserStore.getState();
      
      // If profile exists in store, return it
      if (profile) {
        log.info('[UserService] ✅ Using cached profile from userStore (no API call!)');
        return profile;
      }
      
      // Otherwise fetch via store (which has deduplication)
      log.info('[UserService] Profile not in cache, fetching via userStore');
      await fetchProfile();
      
      let updatedProfile = useUserStore.getState().profile;
      if (!updatedProfile) {
        // fetchProfile() may have returned early because the auth token was expired or
        // a pre-auth 401 was in-progress. setAuth0Id() triggers a separate fetchProfile()
        // once auth completes, so subscribe to the store and wait up to 5s for it to land.
        updatedProfile = await new Promise<UserProfile | null>(resolve => {
          const timeout = setTimeout(() => {
            unsubscribe();
            resolve(null);
          }, 5000);
          const unsubscribe = useUserStore.subscribe(state => {
            if (state.profile) {
              clearTimeout(timeout);
              unsubscribe();
              resolve(state.profile);
            }
          });
        });
      }

      if (!updatedProfile) {
        throw new Error('Failed to fetch user profile');
      }
      
      return updatedProfile;
    } catch (error) {
      log.error('[UserService] Error fetching profile:', error);
      throw error;
    }
  }

  async updateUserProfile(updates: UserProfileUpdate): Promise<UserProfile> {
    try {
      const response = await fetchService(`${this.baseUrl}/profile`, {
        method: 'PUT',
        body: JSON.stringify(updates),
        credentials: 'include',
      });
      // Keep the userStore cache in sync so subsequent getUserProfile() calls
      // within the same session reflect the latest saved state.
      try {
        const useUserStore = (await import('../../stores/userStore')).default;
        if (useUserStore.getState().profile) {
          useUserStore.getState().setProfile(response);
        }
      } catch (_) {
        // Non-fatal: cache update failure doesn't affect the actual save.
      }
      return response;
    } catch (error) {
      log.error('[UserService] Error updating profile:', error);
      throw error;
    }
  }

  async updatePhoneNumber(phoneNumber: string): Promise<void> {
    try {
      await fetchService(`${this.baseUrl}/update-phone`, {
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
        credentials: 'include',
      });
    } catch (error) {
      log.error('[UserService] Error updating phone number:', error);
      throw error;
    }
  }

  async getUsage(): Promise<BackendUserSchema> {
    try {
      // Backend now returns full User entity following entities.py convention
      const response = await fetchService(`${this.baseUrl}/usage`, {
        credentials: 'include',
      });
      return response;
    } catch (error) {
      log.error('[UserService] Error fetching usage:', error);
      throw error;
    }
  }

  async resetUsage(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetchService(`${this.baseUrl}/reset-usage`, {
        method: 'POST',
        credentials: 'include',
      });
      log.log('[UserService] Usage reset successful');
      return response;
    } catch (error) {
      log.error('[UserService] Error resetting usage:', error);
      throw error;
    }
  }

  async setAcceptedEditsCount(count: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetchService(`${this.baseUrl}/set-accepted-edits`, {
        method: 'POST',
        body: JSON.stringify({ count }),
        credentials: 'include',
      });
      log.log('[UserService] Accepted edits count set successfully');
      return response;
    } catch (error) {
      log.error('[UserService] Error setting accepted edits count:', error);
      throw error;
    }
  }

  async downgradeToFreemium(): Promise<{ success: boolean; message: string; previous_plan?: string; current_plan?: string }> {
    try {
      const response = await fetchService(`${this.baseUrl.replace('/user', '')}/stripe/downgrade-to-freemium`, {
        method: 'POST',
        credentials: 'include',
      });
      log.log('[UserService] Successfully downgraded to freemium');
      return response;
    } catch (error) {
      log.error('[UserService] Error downgrading to freemium:', error);
      throw error;
    }
  }

  async getUsersBulk(auth0Ids: string[]): Promise<UserProfile[]> {
    try {
      const response = await fetchService(`${this.baseUrl}/bulk`, {
        method: 'POST',
        body: JSON.stringify({ auth0Ids }),
        credentials: 'include',
      });
      // Handle both old format (array) and new format ({users: array, total: number})
      if (Array.isArray(response)) {
        return response;
      } else if (response && Array.isArray(response.users)) {
        return response.users;
      } else {
        log.error('[UserService] Unexpected response format:', response);
        return [];
      }
    } catch (error) {
      log.error('[UserService] Error fetching users bulk:', error);
      throw error;
    }
  }


  async resetOnboardingState(): Promise<UserProfile> {
    try {
      const resetOnboardingState = {
        checklist: {
          getStarted: false,
          createDocument: false,
          promptEdit: false,
          acceptAutocomplete: false,
          askQuestion: false,
          chatWithDataspace: false,
          joinCommunity: false,
          completed: false,
        },
        taskCounts: {
          createDocument: 0,
          promptEdit: 0,
        },
        currentStep: 0,
        startedAt: new Date().toISOString(),
      };

      const response = await fetchService(`${this.baseUrl}/profile`, {
        method: 'PUT',
        body: JSON.stringify({ onboardingState: resetOnboardingState }),
        credentials: 'include',
      });
      
      log.log('[UserService] Onboarding state reset successful');
      return response;
    } catch (error) {
      log.error('[UserService] Error resetting onboarding state:', error);
      throw error;
    }
  }

  async incrementTokenUsage(inputTokens: number, outputTokens: number): Promise<{ success: boolean; newUsage: any; message?: string }> {
    try {
      const response = await fetchService(`${this.baseUrl}/increment-token-usage`, {
        method: 'POST',
        body: JSON.stringify({ inputTokens, outputTokens }),
        credentials: 'include',
      });
      
      log.log(`[UserService] Token usage incremented: +${inputTokens} input, +${outputTokens} output`);
      return response;
    } catch (error: any) {
      // Handle token limit exceeded errors
      if (error.status === 403 && error.error === 'TOKEN_LIMIT_EXCEEDED') {
        log.warn('[UserService] Token limit exceeded:', error);
        return {
          success: false,
          newUsage: null,
          message: error.message
        };
      }
      
      log.error('[UserService] Error incrementing token usage:', error);
      throw error;
    }
  }

  async getDefaultModel(): Promise<string | null> {
    try {
      const profile = await this.getUserProfile();
      return profile.skeleton.preferences?.defaultModel || null;
    } catch (error) {
      log.error('[UserService] Error getting default model:', error);
      return null;
    }
  }

  async setDefaultModel(model: string): Promise<void> {
    try {
      await this.updateUserProfile({
        preferences: {
          defaultModel: model
        }
      });
      log.log(`[UserService] Default model set to: ${model}`);
    } catch (error) {
      log.error('[UserService] Error setting default model:', error);
      throw error;
    }
  }

  async getUserTimezone(): Promise<string | null> {
    try {
      const profile = await this.getUserProfile();
      return profile.skeleton.preferences?.timezone || null;
    } catch (error) {
      log.error('[UserService] Error getting user timezone:', error);
      return null;
    }
  }

  async setUserTimezone(timezone: string): Promise<void> {
    try {
      await this.updateUserProfile({
        preferences: {
          timezone: timezone
        }
      });
      log.log(`[UserService] User timezone set to: ${timezone}`);
    } catch (error) {
      log.error('[UserService] Error setting user timezone:', error);
      throw error;
    }
  }

  async updateUserPreferences(preferences: Record<string, any>): Promise<void> {
    try {
      // Fetch current profile to merge preferences
      const currentProfile = await this.getUserProfile();
      const currentPreferences = currentProfile?.skeleton?.preferences || {};
      
      // Merge new preferences with existing ones
      const mergedPreferences = {
        ...currentPreferences,
        ...preferences
      };
      
      log.log(`[UserService] Merging preferences:`, {
        current: currentPreferences,
        new: preferences,
        merged: mergedPreferences
      });
      
      await this.updateUserProfile({
        preferences: mergedPreferences
      });
      
      log.log(`[UserService] User preferences updated successfully`);
    } catch (error) {
      log.error('[UserService] Error updating user preferences:', error);
      throw error;
    }
  }

  async closeAccount(): Promise<{ success: boolean; locked: boolean; message: string }> {
    try {
      log.warn('[UserService] ⚠️ Closing user account (soft lock)');
      const response = await fetchService(`${this.baseUrl}/delete-account`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmation: 'DELETE MY ACCOUNT' }),
        credentials: 'include',
      });
      log.warn('[UserService] ✅ Account closed successfully');
      return response;
    } catch (error) {
      log.error('[UserService] Error closing account:', error);
      throw error;
    }
  }
}

export const userService = new UserService(); 